import { Box, Checkbox, FormControlLabel, IconButton, InputAdornment, Link, MenuItem } from "@mui/material";
import React, { useEffect } from "react";
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import ArrowCircleLeft from '@mui/icons-material/ArrowCircleLeft';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { LoadingButton } from "@mui/lab";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
    GradientBackground,
    CenteredContainer,
    GlassCard,
    StyledCardContent,
    LogoCircle,
    GradientTitle,
    StyledSubtitle,
    StyledInputLabel,
    StyledInput,
    StyledSelect,
    FormContainer
} from "../../components/styled/StyledComponents";
import { useLoginController } from "./context/login-controller";
import { useLoginState } from "./context/login-context";
import { useGlobalState } from "../global/context/global-context";
import { useNavigate } from "react-router-dom";
import { useBaseLayoutState } from "../base-layout/context/base-layout-context";

export const Login = () => {
    const { authentication, handleOnChange, resetState, setShowPassword, setInfoAuthentication } = useLoginController();
    const { preAuthenticationRequest, preAuthenticationResponse, authenticationRequest, fieldIsValid, showPassword, authenticationResponse } = useLoginState();
    const { loading } = useBaseLayoutState();
    const { applicationData } = useGlobalState();
    const navigate = useNavigate();

    useEffect(() => {
        if (setInfoAuthentication()) {
            navigate(`/${applicationData?.initialPage}`);
        }
    }, [authenticationResponse])

    return (
        <React.Fragment>
            <GradientBackground>
                <CenteredContainer maxWidth="sm">
                    <GlassCard variant="outlined">
                        <StyledCardContent>
                            <FormContainer>
                                {/* Header com logo e título */}
                                <Box sx={{ textAlign: 'center', mb: 4 }}>
                                    <LogoCircle>M</LogoCircle>
                                    <GradientTitle variant="h4" sx={{ mb: 1 }}>
                                        {applicationData?.moduleDescription}
                                    </GradientTitle>
                                    <StyledSubtitle variant="body1" sx={{ color: 'text.secondary', fontSize: '0.95rem' }}>
                                        Faça login para acessar o sistema
                                    </StyledSubtitle>
                                </Box>
                                {/* Tipo de Login */}
                                <Box sx={{ display: applicationData?.moduleId === 2 ? 'block' : 'none', mb: 2.5 }}>
                                    <StyledInputLabel sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>
                                        Tipo de Login
                                    </StyledInputLabel>
                                    <StyledSelect
                                        name="select-type"
                                        defaultValue={1}
                                        size="medium"
                                        value={preAuthenticationRequest?.type}
                                        error={!fieldIsValid && preAuthenticationRequest?.type === undefined ? true : false}
                                        fullWidth
                                        onChange={handleOnChange}
                                        disabled={(preAuthenticationResponse != null && preAuthenticationResponse?.companies?.length > 0) || loading ? true : false}
                                    >
                                        <MenuItem value={1} key={1}>Entidade</MenuItem>
                                        <MenuItem value={2} key={2}>Empresa</MenuItem>
                                    </StyledSelect>
                                </Box>
                                {/* E-mail */}
                                <Box sx={{ mb: 2.5 }}>
                                    <StyledInputLabel sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>
                                        E-mail
                                    </StyledInputLabel>
                                    <StyledInput
                                        name="input-user"
                                        fullWidth
                                        size="medium"
                                        placeholder="Digite seu e-mail"
                                        error={!fieldIsValid && (preAuthenticationRequest?.email === undefined || preAuthenticationRequest?.email.length === 0) ? true : false}
                                        value={preAuthenticationRequest?.email}
                                        disabled={(preAuthenticationResponse != null && preAuthenticationResponse?.companies?.length > 0) || loading ? true : false}
                                        onChange={handleOnChange}
                                    />
                                </Box>
                                {/* Senha */}
                                <Box sx={{ mb: 2.5 }}>
                                    <StyledInputLabel sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>
                                        Senha
                                    </StyledInputLabel>
                                    <StyledInput
                                        name="input-password"
                                        size="medium"
                                        value={preAuthenticationRequest?.password}
                                        placeholder="Digite sua senha"
                                        error={!fieldIsValid && (preAuthenticationRequest?.password === undefined || preAuthenticationRequest?.password.length === 0) ? true : false}
                                        fullWidth
                                        onChange={handleOnChange}
                                        type={showPassword ? 'text' : 'password'}
                                        disabled={(preAuthenticationResponse != null && preAuthenticationResponse?.companies?.length > 0) || loading ? true : false}
                                        endAdornment={<InputAdornment position='end'>
                                            <IconButton
                                                aria-label='toggle password visibility'
                                                onClick={() => {
                                                    setShowPassword(!showPassword);
                                                }}
                                                edge="end"
                                            >
                                                {!showPassword && <VisibilityIcon />}
                                                {showPassword && <VisibilityOffIcon />}
                                            </IconButton>
                                        </InputAdornment>}
                                    />
                                </Box>
                                {/* Seleção de Empresa/Entidade */}
                                <Box sx={{ display: preAuthenticationResponse != null && preAuthenticationResponse?.companies?.length > 0 ? 'block' : "none", mb: 2.5 }}>
                                    <StyledInputLabel sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>
                                        {preAuthenticationRequest?.type === 1 ? "Entidade" : "Empresa"}
                                    </StyledInputLabel>
                                    <StyledSelect
                                        name="select-company"
                                        size="medium"
                                        value={authenticationRequest?.companyId}
                                        error={!fieldIsValid && (authenticationRequest?.companyId === undefined || authenticationRequest?.companyId === 0) ? true : false}
                                        fullWidth
                                        onChange={handleOnChange}
                                    >
                                        {preAuthenticationResponse?.companies?.map((element) => {
                                            return (
                                                <MenuItem value={element.id} key={element.id}>{element.name} - {element.formattedDocument}</MenuItem>
                                            );
                                        })}
                                    </StyledSelect>
                                </Box>
                                {/* Checkbox e Link "Esqueceu senha" */}
                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={authenticationRequest?.remember || false}
                                                name="check-remember"
                                                onChange={handleOnChange}
                                                disabled={preAuthenticationResponse != null && preAuthenticationResponse?.companies?.length > 0 ? true : false}
                                                size="small"
                                                sx={{
                                                    color: 'primary.main',
                                                    '&.Mui-checked': {
                                                        color: 'primary.main',
                                                    },
                                                }}
                                            />
                                        }
                                        label="Lembrar-me"
                                        sx={{
                                            color: 'text.secondary',
                                            fontSize: '0.875rem',
                                            '& .MuiFormControlLabel-label': {
                                                fontSize: '0.875rem'
                                            }
                                        }}
                                    />
                                    <Link
                                        sx={{
                                            color: 'primary.main',
                                            textDecoration: 'none',
                                            fontWeight: 600,
                                            fontSize: '0.875rem',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                textDecoration: 'underline'
                                            }
                                        }}
                                        href="#"
                                    >
                                        Esqueceu a senha?
                                    </Link>
                                </Box>
                                {/* Botões de ação */}
                                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                    {preAuthenticationResponse != null && preAuthenticationResponse?.companies?.length > 0 && (
                                        <LoadingButton
                                            loading={loading}
                                            loadingPosition="start"
                                            startIcon={<ArrowCircleLeft />}
                                            variant="outlined"
                                            onClick={() => {
                                                resetState();
                                            }}
                                            fullWidth
                                            sx={{
                                                borderRadius: 1,
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                py: 1.5,
                                                fontSize: '0.95rem'
                                            }}
                                        >
                                            Voltar
                                        </LoadingButton>
                                    )}
                                    <LoadingButton
                                        loading={loading}
                                        variant="contained"
                                        loadingPosition="end"
                                        fullWidth
                                        endIcon={(applicationData !== undefined && preAuthenticationRequest?.type === 2) || (preAuthenticationResponse != null && preAuthenticationResponse?.companies?.length > 0 && preAuthenticationRequest?.type === 1) ? <CheckCircleIcon /> : <ArrowCircleRightIcon />}
                                        onClick={authentication}
                                        sx={{
                                            borderRadius: 1,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            py: 1.5,
                                            fontSize: '0.95rem',
                                            boxShadow: 'none',
                                            '&:hover': {
                                                boxShadow: 'none',
                                            }
                                        }}
                                    >
                                        {(applicationData !== undefined && preAuthenticationRequest?.type === 2) || (preAuthenticationResponse != null && preAuthenticationResponse?.companies?.length > 0 && preAuthenticationRequest?.type === 1) ? "Entrar"
                                            : "Próximo"}
                                    </LoadingButton>
                                </Box>
                            </FormContainer>
                        </StyledCardContent>
                    </GlassCard>
                </CenteredContainer>
            </GradientBackground>
        </React.Fragment>
    )
}